import React, { useState, useEffect } from 'react';
import { EeDrawer } from 'components/EeDrawer/EeDrawer';
import { EeModal } from 'components/EeModal/EeModal';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { KPIEditor } from './KPIEditor';
import { Button, Divider, Typography } from '@material-ui/core';
import { useLibrary } from '../hooks/useLibrary';
// @ts-ignore
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
// @ts-ignore
import * as yup from 'yup';
import { toJS } from 'mobx';

export interface KPIDrawerProps {
  open: boolean;
  onClose: any;
  onSave: any;
  onKPIEditorBlur: any;
  groupOptions: any;
  isEditMode: boolean;
  typeOptions: any;
}

export const KPIDrawer = observer(
  ({
    open,
    onClose,
    onSave,
    onKPIEditorBlur,
    groupOptions,
    isEditMode,
    typeOptions,
  }: KPIDrawerProps) => {
    const { models } = useLibrary();
    const [modalOpen, setModalOpen] = useState(false);
    const [kpis, setKpis] = useState(models.editorKPIs);

    const handleClose = () => {
      setModalOpen(false);
      onClose();
    };

    useEffect(() => {
      setKpis(models.editorKPIs);
      return () => {
        reset();
      };
    }, [models.editorKPIs]);

    const formSchema = {
      name: yup
        .string()
        .required('Please give the KPI a name')
        .test(
          'unique',
          'KPI already exists with this name, please choose another',
          // @ts-ignore
          (value: string) => !isKpiExist(value),
        ),
      group: yup
        .string()
        .nullable()
        .required('Please assign the KPI to a group'),
      type: yup.string().required('Please give the KPI a type'),
    } as any;

    const fieldsSchema = yup.object().shape({
      kpiEditor: yup
        .array()
        .of(yup.object().shape(formSchema))
        .required('Must have fields')
        .min(1, 'Minimum of 1 field'),
    });

    const methods = useForm({
      //@ts-ignore
      resolver: yupResolver(fieldsSchema),
      shouldUnregister: false,
      defaultValues: {
        kpiEditor: [{ name: '' }],
      },
      mode: 'onBlur',
    });

    const { control, handleSubmit, getValues, reset } = methods;
    const { fields, append, remove } = useFieldArray({
      control,
      name: 'kpiEditor',
    });

    const saveKpis = () => {
      handleSubmit((data: any) => {
        onSave(isEditMode, data.kpiEditor);
        // An example of setting error for specific file in case of an error from the server side
        /////////////////////////////////////////////////////////////////////////////////////////
        //   const existingKpi = "mykpi";
        //   const formValues = getValues().kpiEditor;
        //   for (let index = 0; index < formValues.length; index++) {
        //     const element = formValues[index];
        //     if(element.name === existingKpi) {
        //       setError(`kpiEditor[${index}].name`, {message: "Unique name error"} );
        //     }
        // }
      })();
    };

    const isKpiExist = (value: string) => {
      if (isEditMode) {
        return models.kpis
          .filter((k: any) => k.name !== kpis[0].name)
          .some((k: any) => k.name === value);
      } else {
        const { kpiEditor } = getValues();
        const hasSameNameSaved = models.kpis.some((k: any) => k.name === value);
        const sameNameUnSavedKPIs = kpiEditor.filter(
          (k: any) => k.name === value,
        );
        const hasSameNameUnSaved = sameNameUnSavedKPIs.length > 1;
        return hasSameNameSaved || hasSameNameUnSaved;
      }
    };

    return (
      <EeDrawer open={open} onClose={() => setModalOpen(true)}>
        <EeModal
          open={modalOpen}
          title={isEditMode ? 'Close Edit KPI' : 'Close Create New KPI'}
          submitTitle="Close without saving"
          onClose={() => setModalOpen(false)}
          onSubmit={handleClose}
        >
          <p>
            Do you want to close <b>{isEditMode ? 'Edit' : 'Create New'} KPI</b>{' '}
            window without saving? The data you entered will not be saved.
          </p>
        </EeModal>

        <div className="d-flex align-items-center mt-3">
          <Typography variant="h2" className="flex-grow-1 mr-3">
            {isEditMode ? 'Edit' : 'Create New'} KPI
          </Typography>
          <Button
            color="primary"
            onClick={() => setModalOpen(true)}
            className="mr-2"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => saveKpis()}
          >
            Save
          </Button>
        </div>
        <FormProvider {...methods}>
          {fields.map((kpi: any, index: number) => {
            return (
              <KPIEditor
                kpi={toJS(kpis)[0]}
                index={index}
                groupOptions={groupOptions}
                typeOptions={typeOptions}
                key={kpi.id}
                // @ts-ignore
                isEditMode={isEditMode}
                onChange={onKPIEditorBlur}
                onDelete={remove}
              />
            );
          })}
        </FormProvider>
        <Divider style={{ backgroundColor: '#2f3b4c' }} />
        {!isEditMode && (
          <div className="d-flex">
            <Button color="primary" onClick={() => append({})}>
              + CREATE NEW KPI
            </Button>
          </div>
        )}
      </EeDrawer>
    );
  },
);
